import type { ActingModifier } from '@/shared/api'
import { DEFAULT_PAGE_SIZE } from '@/shared/lib'
import type { CatalogOfferFilter, CatalogOrder, CatalogProductsQuery, CatalogSort } from './types'

/** Параметры выдачи без модификатора: их же пишет адрес страницы `/catalog`. */
export type CatalogListQuery = Omit<CatalogProductsQuery, keyof ActingModifier>

export const CATALOG_SORTS = [
  'name',
  'price',
  'available',
  'code',
] as const satisfies readonly CatalogSort[]

export const CATALOG_ORDERS = ['asc', 'desc'] as const satisfies readonly CatalogOrder[]

/** Значения отбора `offer` в порядке селекта «Предложения». */
export const CATALOG_OFFERS = [
  'promo',
  'sale',
  'new',
  'incoming',
] as const satisfies readonly CatalogOfferFilter[]

/** Умолчания бэка: равные им значения в адрес не пишутся. */
export const CATALOG_DEFAULTS = {
  sort: 'price',
  order: 'asc',
  page: 1,
  pageSize: DEFAULT_PAGE_SIZE,
} as const satisfies CatalogListQuery

/** Длина `search`, которую ручка принимает; короче или длиннее — `400`. */
export const CATALOG_SEARCH_MIN = 3
export const CATALOG_SEARCH_MAX = 255

/**
 * Отбор выдачи. У бэка их четыре, и они взаимоисключающие по старшинству: справочники
 * (`section`/`model`), поиск, фильтр по значению свойства узла (`param`/`value` с `categoryId`),
 * узел дерева. Младший вместе со старшим — `400`, поэтому новый отбор снимает прежний.
 * Признак (`offer`) — не отбор, а сужение любого из них: он переживает смену отбора.
 */
export type CatalogSelection =
  | { kind: 'all' }
  | { kind: 'node'; categoryId: string }
  | { kind: 'search'; search: string }
  | { kind: 'reference'; section?: string; model?: string }
  | { kind: 'filter'; categoryId: string; param: string; value: string }

/** Срез с новым отбором: порядок, размер страницы и признак остаются, страница — первая. */
export function withSelection(
  query: CatalogListQuery,
  selection: CatalogSelection,
): CatalogListQuery {
  const kept: CatalogListQuery = {
    ...(query.sort !== undefined ? { sort: query.sort } : {}),
    ...(query.order !== undefined ? { order: query.order } : {}),
    ...(query.pageSize !== undefined ? { pageSize: query.pageSize } : {}),
    ...(query.offer !== undefined ? { offer: query.offer } : {}),
  }
  switch (selection.kind) {
    case 'node':
      return { ...kept, categoryId: selection.categoryId }
    case 'search':
      return { ...kept, search: selection.search }
    case 'reference':
      return {
        ...kept,
        ...(selection.section !== undefined ? { section: selection.section } : {}),
        ...(selection.model !== undefined ? { model: selection.model } : {}),
      }
    case 'filter':
      return {
        ...kept,
        categoryId: selection.categoryId,
        param: selection.param,
        value: selection.value,
      }
    default:
      return kept
  }
}

/** Срез с другим признаком (`undefined` — без признака): отбор остаётся, страница — первая. */
export function withOffer(
  query: CatalogListQuery,
  offer: CatalogOfferFilter | undefined,
): CatalogListQuery {
  const { offer: _previous, page: _page, ...rest } = query
  return offer === undefined ? rest : { ...rest, offer }
}
